import { FC, useEffect, useState } from "react";
import CustomSelect from "../../../components/forms/CustomSelect";
import RequiredInput from "../../../components/forms/InputHOC";
import { toast } from "sonner";
import { useFormik } from "formik";
import useHandleErrors from "../../../hooks/useHandleErrors";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import * as yup from "yup";
import { useAtom } from "jotai";
import { formsSubmitAtom } from "../../../atom/atom";
import { Input, Textarea } from "rizzui";
import { Product } from "../../../type/entities/product-type";
import { ProductService } from "../../../services/product/productService";
import useCategories from "../../../hooks/api-calls/useCategories";
import useUnitsMeasure from "../../../hooks/api-calls/useUntisMeasure";

interface ProductFormProps {    
    setOpenModal: any;
    refetch: () => void;
    product?: Product | null;
    formRef?: any;
    refetchAlert: () => void;
}

const schema = yup.object({
    name: yup.string().required('El nombre es obligatorio'),
    description: yup.string().optional(),
    category: yup.string().required('La categoría es obligatoria'),
    unitMeasure: yup.string().required('La unidad de medida es obligatoria'),
    purchasePrice: yup.number().required('El precio de compra es obligatorio').min(0, 'El precio de compra no puede ser negativo'),
    currentStock: yup.number().required('El stock actual es obligatorio').min(0, 'El stock actual no puede ser negativo'),
    minimumStock: yup.number().optional(),
})

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))

const ProductForm:FC<ProductFormProps> = ({ setOpenModal, refetch, product, formRef, refetchAlert }) => {

    // STATES

    const service = new ProductService();
    const isEdit = product !== null;
    const [isSearched, setIsSearched] = useState(false);
    
    // HOOKS
    
    const [, setIsSubmitting]       = useAtom(formsSubmitAtom);
    const { handleErrors }          = useHandleErrors();
    const { getCompaniesList }      = useCompanies();
    const { getCategoriesList }     = useCategories();
    const { getUnitsMeasureList }   = useUnitsMeasure();
    const { userCan }               = usePrivilege();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: product?.id ?? '',
            name: product?.name ?? '',
            description: product?.description ?? '',
            category: product?.category?.id ?? '',
            unitMeasure: product?.unitMeasure?.id ?? '',
            purchasePrice: product?.purchasePrice ?? 0,
            currentStock: product?.currentStock ?? 0,
            minimumStock: product?.minimumStock ?? 0,
            company: product?.company?.id ?? '',
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => { 
            if(!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });


    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UN NUEVO PRODUCTO
     * @EN CREATE A NEW PRODUCT
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = await (await service.createProduct(values)).getResponseData();
            if (response.success) {
                refetch();
                refetchAlert();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Producto creado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------
    

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UN PRODUCTO
     * @EN EDIT A PRODUCT   
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const response = await (await service.editProduct(values)).getResponseData();   
            if (response.success) {
                refetch();
                refetchAlert();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Producto editado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // USE EFFECT

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            setIsSearched(true);
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

    // RENDER

    return (
         <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 gap-4'>

                        <RequiredInput
                            id='name'
                            label="Nombre"
                            placeholder="Ingrese el nombre del producto"
                            className='md:col-span-12'
                            formik={formik}
                            error={formik.errors.name}  
                        />

                        <CustomSelect
                            required
                            isSearchable
                            id={'category'}
                            label="Categoría"
                            customOption={(option: any) => (
                                <div>
                                    <div className='flex items-center'>
                                        <div className='w-4 h-4 rounded border border-white' style={{ backgroundColor: option.color }}></div>
                                        <div className='ml-2'>{option.label}</div>
                                    </div>
                                </div>
                            )}
                            containerClassName='md:col-span-12'
                            value={{ value: formik.values.category, label: getCategoriesList()?.find((g: any) => g.value === formik.values.category)?.label, color: getCategoriesList()?.find((g: any) => g.value === formik.values.category)?.color }}
                            options={getCategoriesList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('category', e?.value) }
                            }
                            error={formik.errors.category}
                        />

                        <RequiredInput
                            id='currentStock'
                            label="Stock actual"
                            type='number'
                            className='md:col-span-6'
                            formik={formik}
                            error={formik.errors.currentStock}  
                        />

                        <RequiredInput
                            id='minimumStock'
                            label="Stock mínimo"
                            type='number'
                            className='md:col-span-6'
                            formik={formik}
                            error={formik.errors.minimumStock}  
                        />

                        <CustomSelect
                            required
                            isSearchable
                            id={'unitMeasure'}
                            label="Unidad de medida"
                            placeholder="Selecciona la unidad de medida"
                            containerClassName='md:col-span-12'
                            value={{ value: formik.values.unitMeasure, label: getUnitsMeasureList()?.find((g: any) => g.value === formik.values.unitMeasure)?.label }}
                            options={getUnitsMeasureList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('unitMeasure', e?.value) }
                            }
                            error={formik.errors.unitMeasure}
                        />

                        <RequiredInput
                            id='purchasePrice'
                            label="Precio de compra"
                            className='md:col-span-12'
                            formik={formik}
                            type={'number'}
                            error={formik.errors.purchasePrice}
                            placeholder="Ingresa el precio de compra"
                            suffix="€"
                        />

                        {userCan('list', 'company') && (
                            <CustomSelect
                            required
                            isSearchable
                            id={'company'}
                            label="Compañia"
                            containerClassName='md:col-span-12'
                            value={
                                formik.values.company ?
                                { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                            }
                            options={getCompaniesList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('company', e?.value) }
                            }
                            error={formik.errors.company}
                            />
                        )}

                        <Textarea
                            id='description'
                            label='Descripción'
                            rows={4}
                            value={formik.values.description}
                            onChange={(e) => formik.setFieldValue('description', e.target.value)}
                            className="mb-4 md:col-span-12"
                        />
                    </div>
                </div>
            </form>
        </div>
    );
}

export default ProductForm;
